import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { yupResolver } from '@hookform/resolvers/yup';
import {
	Drawer,
	Box,
	Typography,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from '@mui/material';

import { CancelButton } from 'src/components/common/buttons/CancelButton';
import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { ICreateUserFormData } from 'src/features/user/domain/types';
import { ICreateUserDrawerProps } from 'src/features/user/domain/types';
import { CreateUserValidationSchema } from 'src/features/user/domain/validation';
import { USER_LOADING_KEY } from 'src/features/user/store/userThunks';

export const CreateUserDrawer: React.FC<ICreateUserDrawerProps> = ({
	open,
	onClose,
	onSubmit,
}) => {
	const isLoading = useSelector(
		(state: { loading: ILoadingState }) =>
			state.loading.loadingStates[USER_LOADING_KEY],
	);

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<ICreateUserFormData>({
		resolver: yupResolver(CreateUserValidationSchema),
		defaultValues: {
			firstName: '',
			lastName: '',
			type: '',
			email: '',
			password: '',
		},
	});

	useEffect(() => {
		if (open) {
			reset();
		}
	}, [open, reset]);

	if (!open) return null;

	return (
		<Drawer
			anchor='right'
			open={open}
			onClose={onClose}
			sx={{
				'& .MuiDrawer-paper': {
					width: '40%',
					boxSizing: 'border-box',
					padding: '32px',
					'@media (max-width: 600px)': {
						width: '100%',
					},
				},
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 3,
				}}
			>
				<Typography variant='h6' component='h2'>
					Create new user
				</Typography>
			</Box>

			<form onSubmit={handleSubmit(onSubmit)}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					<Controller
						name='firstName'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='First Name'
								variant='outlined'
								fullWidth
								error={!!errors.firstName}
								helperText={errors.firstName?.message}
							/>
						)}
					/>
					<Controller
						name='lastName'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Last Name'
								variant='outlined'
								fullWidth
								error={!!errors.lastName}
								helperText={errors.lastName?.message}
							/>
						)}
					/>
					<Controller
						name='type'
						control={control}
						render={({ field }) => (
							<FormControl fullWidth error={!!errors.type}>
								<InputLabel>Type</InputLabel>
								<Select {...field} label='Type'>
									<MenuItem value='superadmin'>
										Super Admin
									</MenuItem>
									<MenuItem value='general'>General</MenuItem>
								</Select>
								{errors.type && (
									<Typography
										color='error'
										variant='caption'
										sx={{ ml: 1.75, mt: 0.5 }}
									>
										{errors.type.message}
									</Typography>
								)}
							</FormControl>
						)}
					/>
					<Controller
						name='email'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Email'
								variant='outlined'
								fullWidth
								type='email'
								error={!!errors.email}
								helperText={errors.email?.message}
							/>
						)}
					/>
					<Controller
						name='password'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Password'
								variant='outlined'
								fullWidth
								type='password'
								error={!!errors.password}
								helperText={errors.password?.message}
							/>
						)}
					/>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							mt: 1,
						}}
					>
						<PrimaryButton type='submit' isLoading={isLoading}>
							Create
						</PrimaryButton>
						<CancelButton onClick={onClose}>Cancel</CancelButton>
					</Box>
				</Box>
			</form>
		</Drawer>
	);
};
